import React, { useState } from "react";
import { ScreenShell } from "../components";
import type { ImportFromHealthOutcome } from "../types";
import { T } from "../theme";

type Props = {
  step: number;
  total: number;
  onConnect: () => Promise<ImportFromHealthOutcome>;
  onSkip: () => void;
  onBack: (() => void) | null;
  isMobile: boolean;
};

// First-touch pitch for the Apple Health integration. The value prop is
// ongoing sync, not one-time pre-fill — so this screen sells what the
// integration *does for them over time* and stays out of the way of the
// data-collection screens that follow. Apple's own permission sheet
// handles the read/write disclosure, so we don't duplicate it here.
export function HealthKitConnectScreen({
  step,
  total,
  onConnect,
  onSkip,
  onBack,
  isMobile,
}: Props): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);

  const handleConnect = async () => {
    if (busy) return;
    setBusy(true);
    setRetryMessage(null);
    try {
      const result = await onConnect();
      switch (result.kind) {
        case "imported":
        case "noData":
          onSkip();
          return;
        case "denied":
          setRetryMessage("Connection cancelled. You can connect anytime from Settings.");
          break;
        case "blocked":
          setRetryMessage("Apple Health access is off. Turn it on in iOS Settings → Health → Data Access & Devices.");
          break;
        case "error":
        default:
          setRetryMessage("Couldn't reach Apple Health. Try again, or skip for now.");
          break;
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScreenShell
      step={step}
      total={total}
      title="Skip the typing."
      subtitle="Sync weight and active calories from Apple Health so your daily targets stay accurate without manual logging."
      onBack={onBack}
      isMobile={isMobile}
      primaryLabel={busy ? "Opening Apple Health…" : "Connect Apple Health"}
      primaryDisabled={busy}
      onPrimary={handleConnect}
      secondaryLabel="Maybe later"
      onSecondary={busy ? undefined : onSkip}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18, marginTop: 12 }}>
        <div
          style={{
            width: 112,
            height: 112,
            borderRadius: 28,
            background: "#FFFFFF",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
          }}
        >
          <img
            src="/Icons/apple-health.PNG"
            alt="Apple Health"
            width={92}
            height={92}
            style={{ display: "block", objectFit: "contain" }}
          />
        </div>

        <div
          style={{
            width: "100%",
            display: "grid",
            gap: 12,
            marginTop: 4,
          }}
        >
          <ValueRow
            title="Auto-update your weight trend"
            body="New weigh-ins flow in. No retyping after every weigh-in."
          />
          <ValueRow
            title="Calorie target reacts to your activity"
            body="Walks, workouts, and rings adjust your daily intake."
          />
          <ValueRow
            title="Nothing written back"
            body="One-way read. We never modify your Health data."
          />
        </div>

        {retryMessage ? (
          <div style={{ marginTop: 4, textAlign: "center", color: T.fg2, fontSize: "0.84rem", lineHeight: 1.4 }}>
            {retryMessage}
          </div>
        ) : (
          <div style={{ marginTop: 4, textAlign: "center", color: T.muted, fontSize: "0.78rem" }}>
            You can connect anytime from Settings.
          </div>
        )}
      </div>
    </ScreenShell>
  );
}

function ValueRow({ title, body }: { title: string; body: string }): React.ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "0.7rem 0.85rem",
        borderRadius: 12,
        background: T.bgCard,
        border: `1px solid ${T.hairline}`,
      }}
    >
      <span
        style={{
          flexShrink: 0,
          marginTop: 2,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: 999,
          background: "rgba(132,204,22,0.12)",
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.lime} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "0.95rem", fontWeight: 700, color: T.fg, lineHeight: 1.3 }}>{title}</div>
        <div style={{ marginTop: 2, fontSize: "0.84rem", color: T.fg2, lineHeight: 1.4 }}>{body}</div>
      </div>
    </div>
  );
}
